import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader } from '../ui/card';
import { apiService, ApiError } from '../../services/api';

export function AuthForm() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [managerCredentials, setManagerCredentials] = useState({
    email: '',
    password: '',
  });
  const [managerError, setManagerError] = useState('');
  const [success, setSuccess] = useState('');

  // Helper function to extract error message
  const getErrorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      return err.message;
    } else if (err instanceof Error) {
      return err.message;
    } else if (typeof err === 'string') {
      return err;
    }
    return fallback;
  };

  // Manager authentication
  const handleManagerLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setManagerError('');
    setSuccess('');
    setIsLoading(true);
    
    try {
      // Authenticate manager and get JWT tokens
      const response = await apiService.managerAuthenticate(
        managerCredentials.email,
        managerCredentials.password
      );

      // Store manager tokens securely
      apiService.setManagerToken(response.accessToken, response.refreshToken);

      // Store manager info (no password!)
      const managerSession = {
        email: response.manager.email,
        firstName: response.manager.firstName,
        lastName: response.manager.lastName,
        role: response.manager.role,
        managerId: response.manager.id,
        loginTime: new Date().toISOString()
      };
      
      localStorage.setItem('managerSession', JSON.stringify(managerSession));
      console.log('[AuthForm] Manager session stored:', managerSession);
      setSuccess('Welcome back, ' + response.manager.firstName + '! Redirecting...');
      
      // Dispatch custom event to notify App.tsx of manager session change
      window.dispatchEvent(new Event('managerSessionChange'));
      console.log('[AuthForm] managerSessionChange event dispatched');
      
      // Navigate directly to admin console with a longer delay to ensure state updates
      setTimeout(() => {
        console.log('[AuthForm] Navigating to /admin');
        navigate('/admin');
      }, 800);
    } catch (err) {
        const errorMessage = getErrorMessage(err, 'Manager login failed');
        setManagerError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [managerCredentials.email, managerCredentials.password, navigate]);

  // Memoized change handlers
  const handleManagerEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerCredentials(prev => ({ ...prev, email: e.target.value }));
  }, []);

  const handleManagerPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManagerCredentials(prev => ({ ...prev, password: e.target.value }));
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <h2 className="text-2xl font-bold">Manager Login</h2>
            <p className="text-sm text-muted-foreground">Sign in to access the admin console</p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <form onSubmit={handleManagerLogin} className="space-y-4" autoComplete="on" method="post" action="">
              <div className="space-y-2">
                <label htmlFor="manager-email" className="text-sm font-medium">Email</label>
                <Input
                  id="manager-email"
                  name="username"
                  type="email"
                  value={managerCredentials.email}
                  onChange={handleManagerEmailChange}
                  placeholder="manager@example.com"
                  autoComplete="username"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="manager-password" className="text-sm font-medium">Password</label>
                <Input
                  id="manager-password"
                  name="password"
                  type="password"
                  value={managerCredentials.password}
                  onChange={handleManagerPasswordChange}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </form>

            {/* Error Messages */}
            {managerError && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                {managerError}
              </div>
            )}
            
            {/* Success Messages */}
            {success && (
              <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                {success}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}